// Hook para tracking automático de engagement de compradores

import { useEffect, useRef, useCallback } from 'react';
import {
  createEngagementEvent,
  getBrowserMetadata,
  generateSessionId,
} from '../services/supabase/engagement';

/**
 * Hook para tracking automático de engagement en PropertyView
 * Captura eventos de navegación, interacción y comportamiento del comprador
 * Incluye throttling para evitar spam y genera sessionId único por visita
 * @param {string} propertyId - UUID de la propiedad
 * @param {string} inviteToken - Token único de invitación del comprador
 * @param {boolean} [enabled=true] - Si el tracking está habilitado (false para spectators)
 * @returns {Object} Funciones de tracking
 * @returns {Function} returns.trackEvent - Función para registrar evento personalizado
 * @returns {Function} returns.trackPageView - Registra vista de página
 * @returns {Function} returns.trackGalleryView - Registra visualización de galería
 * @returns {Function} returns.trackFloorPlanView - Registra visualización de plano
 * @returns {Function} returns.trackVideoPlay - Registra reproducción de video
 * @returns {Function} returns.trackDocumentDownload - Registra descarga de documento
 * @returns {Function} returns.trackPageExit - Registra salida de página (cleanup)
 */
export const useEngagementTracking = (propertyId, inviteToken, enabled = true) => {
  const sessionIdRef = useRef(null);
  const metadataRef = useRef(null);
  const lastEventRef = useRef(null);
  const lastVisitTimeRef = useRef(null);

  // Generar sessionId y metadata una sola vez al montar
  useEffect(() => {
    if (!enabled || !propertyId || !inviteToken) return;

    sessionIdRef.current = generateSessionId();
    metadataRef.current = getBrowserMetadata();

    console.log('📊 Engagement tracking iniciado:', {
      sessionId: sessionIdRef.current,
      propertyId,
    });
  }, [propertyId, inviteToken, enabled]);

  /**
   * Función helper para enviar eventos
   * Incluye throttling para evitar spam
   */
  const trackEvent = useCallback(
    async (eventType, eventData = {}) => {
      if (!enabled || !propertyId || !inviteToken || !sessionIdRef.current) {
        return;
      }

      // Throttling simple: no enviar el mismo evento dos veces en 1 segundo
      const eventKey = `${eventType}-${JSON.stringify(eventData)}`;
      const now = Date.now();

      if (lastEventRef.current?.key === eventKey && now - lastEventRef.current.time < 1000) {
        return;
      }

      lastEventRef.current = { key: eventKey, time: now };

      try {
        await createEngagementEvent(
          propertyId,
          inviteToken,
          sessionIdRef.current,
          eventType,
          eventData,
          metadataRef.current
        );
      } catch (error) {
        // Silenciar errores - tracking no debe romper la app
        console.debug('Engagement tracking error:', error);
      }
    },
    [propertyId, inviteToken, enabled]
  );

  // ============================================
  // TRACKING AUTOMÁTICO: Page View & Session Duration
  // ============================================

  useEffect(() => {
    if (!enabled || !propertyId || !inviteToken) return;

    // Evento: Entrada a la página
    trackEvent('page_view', {
      referrer: document.referrer,
      url: window.location.href,
    });
    lastVisitTimeRef.current = Date.now();
  }, [propertyId, inviteToken, enabled, trackEvent]);


  // ============================================
  // TRACKING AUTOMÁTICO: Retorno a la página
  // Solo trackea cuando el usuario VUELVE (no cuando se va)
  // ============================================

  useEffect(() => {
    if (!enabled || !propertyId || !inviteToken) return;

    let wasHidden = false;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Usuario se fue - solo marcar flag, no generar evento
        wasHidden = true;
      } else if (wasHidden) {
        // Usuario VOLVIÓ después de haberse ido
        wasHidden = false;
        const now = Date.now();
        const VISIT_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutos
        if (!lastVisitTimeRef.current || now - lastVisitTimeRef.current > VISIT_COOLDOWN_MS) {
          lastVisitTimeRef.current = now;
          trackEvent('page_return', {
            returnedAt: new Date().toISOString(),
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [propertyId, inviteToken, enabled, trackEvent]);

  // Retornar función pública para tracking manual
  return {
    trackEvent,
    sessionId: sessionIdRef.current,
  };
};

/**
 * Hook para tracking de video (DEPRECADO)
 * Los videos son iframes externos (Bunny, Wistia) donde no podemos
 * acceder a eventos play/pause. Solo usamos video_view_start desde PropertyMedia.
 *
 * @deprecated No se usa - los iframes externos no exponen eventos
 */
export const useVideoTracking = () => {
  // Hook vacío - mantenido por compatibilidad
  // El único evento útil es video_view_start que se dispara en PropertyMedia
};

export default useEngagementTracking;